import React, { useState, useEffect } from 'react'
import './accountDetails.scss'
import { Button, Card, Form, Input, Modal } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useDispatch, useSelector } from 'react-redux'
import { api } from '../../../helpers/api_helper'
import { API_ENDPOINTS } from '../../../constants/apiEndPoints'
import { businessInfo } from '../../../slices/businessInfo/thunk'
import { validationContent } from '../../../constants/Validation'
import { Popup_Toast } from '../../../constants/Popups_Toast'
import { PlaceholderContent } from '../../../constants/Placeholder'

const AccountDetails = () => {
    document.title = "Account Details"; 
    const dispatch = useDispatch();
    const [successModal, setSuccessModal] = useState(false);
    const [form] = Form.useForm();

    // Get business info from Redux store (now contains bureau data)
    let businessInfoDetails = useSelector(state => state.BusinessInfoReducer?.businessInfo || {});
    
    // Check if profile is verified
    const isProfileVerified = businessInfoDetails?.profile_verified || false;

    // Validation content
    const content = validationContent?.BUSINESS_INFO_PAGE;
    const popupContent = Popup_Toast?.BUSINESS_INFO_SAVED;
    const placeHolderContent = PlaceholderContent?.BUSINESS_INFO_PAGE;

    // Field status tracking
    const [fieldStatus, setFieldStatus] = useState({
        name: false,
        address: false,
        contact_number: false
    });

    // Field length limits
    const [limit] = useState({
        contact_person: 50,
        address: 200,
        contact_number: 10
    });

    // Current field lengths (keeping for potential future use)
    const [fieldLengths, setFieldLengths] = useState({
        contactPerson: 0,
        businessDescription: 0,
        contactNumber: 0
    });

    // Button disable state
    const [accountDetailsButtonDisable, setAccountDetailsButtonDisable] = useState({
        dataUpdateError: false,
        formError: true
    });

    // Aadhar verification state
    const [aadharVerification, setAadharVerification] = useState({
        loading: false,
        error: null,
        success: false
    });

    // Polling state
    const [isPolling, setIsPolling] = useState(false);
    const pollingIntervalRef = React.useRef(null);
    const lastVisibilityChangeRef = React.useRef(null);

    // Ensure first character is always uppercase while typing
    const capitalizeFirstLetter = (input) => {
        if (typeof input !== 'string' || input.length === 0) return input;
        return input.charAt(0).toUpperCase() + input.slice(1);
    };

    // Load initial data
    useEffect(() => {
        dispatch(businessInfo());
    }, [dispatch]);

    // Page Visibility API - Detect when user returns to the page from DigiLocker
    useEffect(() => {
        const checkVerificationStatus = () => {
            if (isPolling && !isProfileVerified) {
                const now = Date.now();
                const lastCheck = lastVisibilityChangeRef.current;
                
                // Prevent multiple rapid calls (debounce - wait at least 2 seconds between checks)
                if (!lastCheck || (now - lastCheck) > 2000) {
                    console.log('User returned to page - checking verification status immediately...');
                    lastVisibilityChangeRef.current = now;
                    dispatch(businessInfo());
                }
            }
        };

        const handleVisibilityChange = () => {
            // When page becomes visible again and we're waiting for verification
            if (!document.hidden) {
                checkVerificationStatus();
            }
        };

        const handleWindowFocus = () => {
            // When window gains focus (e.g., user closes DigiLocker window/tab)
            checkVerificationStatus();
        };

        // Listen for visibility changes and window focus
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleWindowFocus);

        // Cleanup
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [isPolling, isProfileVerified, dispatch]);

    // Polling effect - check verification status every 30 seconds as fallback
    useEffect(() => {
        // Start polling if verification window was opened and profile is not verified
        if (isPolling && !isProfileVerified) {
            pollingIntervalRef.current = setInterval(() => {
                console.log('Polling bureau status (fallback)...');
                dispatch(businessInfo());
            }, 30000); // 30 seconds
        }

        // Stop polling if profile is verified
        if (isProfileVerified && pollingIntervalRef.current) {
            console.log('Profile verified! Stopping polling.');
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setIsPolling(false);
        }

        // Cleanup on unmount
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [isPolling, isProfileVerified, dispatch]);

    // Update form when business info (bureau data) loads
    useEffect(() => {
        if (businessInfoDetails) {
            const formData = {
                name: businessInfoDetails.name || '',
                address: businessInfoDetails.address || '',
                contact_number: businessInfoDetails.contact_number || ''
            };

            // Set form values
            form.setFieldsValue(formData);

            // Update field lengths
            setFieldLengths({
                contactPerson: formData.name?.length || 0,
                businessDescription: formData.address?.length || 0,
                contactNumber: formData.contact_number?.length || 0
            });

            // Update field status - only required fields affect form submission
            setFieldStatus({
                name: !!formData.name,
                address: !!formData.address,
                contact_number: !!formData.contact_number
            });
        }
    }, [businessInfoDetails, form]);

    // Handle input changes
    const businessInfoModify = (event, fieldName) => {
        let { value } = event.target;

        // Force first character uppercase for specific fields
        if (fieldName === 'name' || fieldName === 'address') {
            const capitalized = capitalizeFirstLetter(value);
            // Reflect the transformed value into the form immediately
            form.setFieldsValue({ [fieldName]: capitalized });
            value = capitalized;
        }

        // Update field lengths
        switch (fieldName) {
            case 'name':
                setFieldLengths(prev => ({ ...prev, contactPerson: value?.length || 0 }));
                break;
            case 'address':
                setFieldLengths(prev => ({ ...prev, businessDescription: value?.length || 0 }));
                break;
            case 'contact_number':
                setFieldLengths(prev => ({ ...prev, contactNumber: value?.length || 0 }));
                break;
            default:
                break;
        }

        // Update field status
        setFieldStatus(prev => ({
            ...prev,
            [fieldName]: !!value
        }));

        // Enable/disable submit button
        setAccountDetailsButtonDisable(prev => ({
            ...prev,
            dataUpdateError: false
        }));
    };

    // Form submission
    const onFinish = async (values) => {
        try {
            // Prepare payload with only the required fields for API
            const payload = {
                name: values.name,
                contact_number: values.contact_number,
                address: values.address
            };

            const response = await api.put(API_ENDPOINTS.BUREAU.UPDATE, payload);
            setSuccessModal(response.message || 'Account details updated successfully');
            dispatch(businessInfo()); // Refresh data
        } catch (error) {
            console.error('Error updating account details:', error);
            // You can add error handling here
        } finally {
            setAccountDetailsButtonDisable(prev => ({
                ...prev,
                formError: true
            }));
        }
    };

    // Handle form field changes for validation
    const checkErrors = () => {
        const errors = form.getFieldsError();
        const hasErrors = errors.some(error => error.errors.length > 0);
        setAccountDetailsButtonDisable(prev => ({
            ...prev,
            formError: hasErrors
        }));
    };

    // Handle modal cancel
    const handleModalCancel = () => {
        setSuccessModal(false);
    };

    // Generate random 12-digit number for Aadhar verification
    const generateRandomAadharNumber = () => {
        // Generate a random 12-digit number
        const randomNumber = Math.floor(100000000000 + Math.random() * 900000000000);
        return randomNumber.toString();
    };

    // Handle DigiLocker verification
    const handleDigiLockerVerification = async () => {
        setAadharVerification({
            loading: true,
            error: null,
            success: false
        });

        try {
            // Generate random Aadhar number
            const randomAadharNumber = generateRandomAadharNumber();
            
            // Call verification API with random aadhar number
            const response = await api.create(`${API_ENDPOINTS.BUREAU.VERIFY}?id_number=${randomAadharNumber}`);

            if (response.success && response.data?.data?.url) {
                // Open DigiLocker verification URL in a new tab
                window.open(response.data.data.url, '_blank');

                setAadharVerification({
                    success: true,
                    loading: false,
                    error: null
                });

                // Start polling to check verification status
                setIsPolling(true);
                console.log('Started polling for verification status...');
            } else {
                throw new Error(response.message || 'Verification failed');
            }
        } catch (error) {
            console.error('DigiLocker verification error:', error);
            setAadharVerification({
                error: error.message || 'Verification failed. Please try again.',
                loading: false,
                success: false
            });
        }
    };

    return (
        <>
            <Card className="business_information_form">
                <div className='account_details_container form_body'>
                    <Form
                        form={form}
                        onFinish={onFinish}
                        onFieldsChange={checkErrors}
                        validateMessages={{
                            required: `\${label} is required!`,
                            types: {
                                email: `\${label} is not a valid email!`,
                                number: `\${label} is not a valid number!`,
                            },
                        }}
                    >
                        <div className={`input_holder ${!fieldStatus?.name && 'warning_status_active'}`}>
                            <label htmlFor="name" className=' d-block input_label'>Bureau/Broker Name</label>
                            <Form.Item
                                name="name"
                                rules={[{ required: true, message: (content?.EnterContactPersonName || "Enter bureau name.") }]}
                                onChange={(event) => businessInfoModify(event, 'name')}
                            >
                                <Input
                                    className='input_field'
                                    placeholder={placeHolderContent?.ContactPerson || 'Enter Bureau Name'}
                                    type="text"
                                    maxLength={limit?.contact_person}
                                    minLength={2}
                                />
                            </Form.Item>
                            {/* <InputFieldMxLength maxValue={limit?.contact_person} valueLength={fieldLengths?.contactPerson} /> */}
                        </div>

                        <div style={{ width: "100%" }} className={`input_holder ${!fieldStatus?.address && 'warning_status_active'}`}>
                            <div className="address_wrap">
                                <label htmlFor="address" className=' d-block input_label'>Address</label>
                                <Form.Item
                                    name="address"
                                    onChange={(event) => businessInfoModify(event, 'address')}
                                    rules={[{ required: true, message: (content?.EnterBusinessDescription || "Enter your Address.") }]}
                                >
                                    <TextArea
                                        maxLength={limit.address}
                                        style={{ margin: "10px 0" }}
                                        rows={5}
                                        className='input_field'
                                        placeholder={(placeHolderContent?.BusinessDescription || 'Add Your Address')}
                                    />
                                </Form.Item>
                                <div className="">
                                    <span className={`form_note ${fieldLengths?.businessDescription === limit?.address ? 'limit' : ''}`}>
                                        <span>{fieldLengths?.businessDescription || 0}</span>/{limit?.address}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="contact_block">
                            <div className={`input_holder ${!fieldStatus?.contact_number && 'warning_status_active'}`}>
                                <label htmlFor="contact_number" className=' d-block input_label'>Contact Number</label>
                                <div className="country_code_wrap">
                                    <span className='country_code'>+91</span>
                                    <Form.Item
                                        name="contact_number"
                                        rules={[
                                            { required: true, message: "Enter your contact number." },
                                            { pattern: /^\d{10}$/, message: 'Please enter a valid 10-digit mobile number' }
                                        ]}
                                        onChange={(event) => businessInfoModify(event, 'contact_number')}
                                    >
                                        <Input
                                            className='input_field'
                                            placeholder={'Enter Contact Number'}
                                            type="text"
                                            maxLength={10}
                                            disabled={true}
                                            onKeyDown={(e) => {
                                                if (!(e.key === 'Backspace' || e.key === 'Delete' || /^\d$/.test(e.key))) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </div>
                                {/* <InputFieldMxLength maxValue={limit?.contact_number} valueLength={fieldLengths?.contactNumber} /> */}
                            </div>
                            <div className="digilocker_verification_section">
                                <label className='d-block  mb-3'>
                                    Verify with DigiLocker
                                    {/* {isProfileVerified && (
                                        <span className="text-success ms-2" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                            <i className="ri-checkbox-circle-fill me-1"></i>Verified
                                        </span>
                                    )} */}
                                </label>
                                
                                {!isProfileVerified ? (
                                    <div className='digilocker_verify_container'>
                                        <p className="verification_info mb-3" style={{ fontSize: '13px', color: '#666' }}>
                                            Click the button below to verify your identity through DigiLocker. After completing verification, return to this page to see the updated status.
                                        </p>
                                        
                                        {aadharVerification.error && (
                                            <div className="text-danger mb-3" style={{ fontSize: '12px', padding: '0.75rem', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '4px' }}>
                                                <i className="ri-error-warning-line me-1"></i>
                                                {aadharVerification.error}
                                            </div>
                                        )}
                                        
                                        {isPolling && (
                                            <div className="verification_pending mb-3">
                                                <i className="ri-time-line me-2"></i>
                                                Waiting for verification... Return to this page after completing DigiLocker verification.
                                            </div>
                                        )}
                                        
                                        <Button
                                            className='digilocker_verify_btn'
                                            type="primary"
                                            size="large"
                                            loading={aadharVerification.loading}
                                            onClick={handleDigiLockerVerification}
                                            disabled={aadharVerification.loading || isPolling}
                                            icon={<i className="ri-shield-check-line me-2"></i>}
                                        >
                                            {aadharVerification.loading ? 'Opening DigiLocker...' : 'Verify with DigiLocker'}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className='profile_verified_status'>
                                        <span className="text-success" style={{ fontSize: '14px' }}>
                                            <i className="ri-shield-check-fill me-2"></i>
                                            Your profile has been verified through DigiLocker
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="form_submit_section">
                            <Form.Item>
                                <Button
                                    type='primary'
                                    htmlType="submit"
                                    disabled={accountDetailsButtonDisable.formError || accountDetailsButtonDisable.dataUpdateError}
                                    className='save_changes_btn'
                                >
                                    Save Changes
                                </Button>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </Card>

            {/* Success Modal */}
            <Modal
                className='sm_modal work_modal center_modal text-center'
                open={successModal}
                onOk={handleModalCancel}
                onCancel={handleModalCancel}
                footer={[
                    <div className='text-center' key="ok">
                        <Button type="primary" className="short wide" onClick={handleModalCancel}>
                            Ok
                        </Button>
                    </div>
                ]}
            >
                <div className="modal_cnt">
                    <h2 className='text_lg text_red'>{(popupContent?.content?.title) || 'Success'}</h2>
                    <p className="md">{(popupContent?.content?.description) || 'Account details updated successfully.'}</p>
                </div>
            </Modal>
            {/* <BusinessInfo/> */}
        </>
    )
}

export default AccountDetails